import React, { type ReactNode, createContext, useCallback, useRef, useState } from 'react';

import ConfirmationDialog from './ConfirmationDialog';

export type ConfirmationOptions = Parameters<typeof ConfirmationDialog>[0]['dialogProps'];

type ConfirmationContextType = {
  confirm: (options: Parameters<typeof ConfirmationDialog>[0]['dialogProps']) => Promise<boolean>;
};

export const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

type ConfirmationProviderProps = {
  children: ReactNode;
};

const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const resolvePromiseRef = useRef<((value: boolean) => void) | null>(null);
  const [dialogProps, setDialogProps] = useState<ConfirmationOptions>({
    message: '',
  });

  const confirm = useCallback(
    (options: ConfirmationOptions): Promise<boolean> =>
      new Promise((resolve) => {
        setDialogProps(options);
        resolvePromiseRef.current = resolve;
        setIsOpen(true);
      }),
    [],
  );

  const handleClose = useCallback((confirmed: boolean) => {
    setIsOpen(false);
    if (resolvePromiseRef.current) {
      resolvePromiseRef.current(confirmed);
      resolvePromiseRef.current = null;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    handleClose(true);
  }, [handleClose]);

  const handleCancel = useCallback(() => {
    handleClose(false);
  }, [handleClose]);

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <ConfirmationDialog isOpen={isOpen} onClose={handleCancel} onConfirm={handleConfirm} dialogProps={dialogProps} />
    </ConfirmationContext.Provider>
  );
};

export default ConfirmationProvider;
